import React from "react";
import {
  Calendar,
  Loader2,
  ShieldCheck,
} from "lucide-react";

interface LegalPageLayoutProps {
  title: string;
  subtitle: string;
  isLoading: boolean;
  isError: boolean;
  contentData: {
    title: string;
    content: string;
    createdOn?: string;
    pageTypeName?: string;
  } | null;
}

export const LegalPageLayout: React.FC<
  LegalPageLayoutProps
> = ({
  title,
  subtitle,
  isLoading,
  isError,
  contentData,
}) => {
  // ============================================================
  // CLEAN HTML
  // ============================================================
  const sanitizeAndCleanHtml = (
    rawHtml: string
  ) => {
    if (!rawHtml) {
      return "";
    }

    return rawHtml
      .replace(/&nbsp;/g, " ")
      .replace(/\u00a0/g, " ");
  };

  // ============================================================
  // LOADING
  // ============================================================
  if (isLoading) {
    return (
      <div
        className="
          min-h-[60vh]
          flex
          flex-col
          items-center
          justify-center
          gap-3
          text-slate-500
          font-medium
        "
      >
        <Loader2
          className="
            w-6
            h-6
            animate-spin
            text-[#2F5E2E]
          "
        />

        <span className="text-sm">
          Loading document...
        </span>
      </div>
    );
  }

  // ============================================================
  // ERROR
  // ============================================================
  if (isError || !contentData) {
    return (
      <div
        className="
          max-w-4xl
          mx-auto
          my-12
          p-8
          text-center
          bg-white
          border
          border-dashed
          border-slate-200
          rounded-2xl
          text-slate-400
          text-sm
          font-semibold
          italic
        "
      >
        <ShieldCheck
          size={32}
          className="
            mx-auto
            mb-3
            text-slate-300
            stroke-[1.5]
          "
        />

        <p>
          This document is currently unavailable.
        </p>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div
      className="
        min-h-screen
        w-full
        bg-white
        text-left
      "
    >
      <div
        className="
          max-w-5xl
          mx-auto
          w-full
          px-4
          sm:px-6
          py-6
          sm:py-8
        "
      >
        <div
          className="
            space-y-5
            animate-in
            fade-in
            duration-200
          "
        >
          {/* ======================================================
              HEADER
          ====================================================== */}

          <div
            className="
              border-b
              border-slate-200
              pb-4
            "
          >
            {/* Page Type */}

            <span
              className="
                text-[11px]
                sm:text-xs
                font-bold
                tracking-[0.14em]
                text-[#2F5E2E]
                uppercase
                block
                mb-1
              "
            >
              {contentData.pageTypeName ||
                title}{" "}
              Document
            </span>

            {/* Page Title */}

            <h1
              className="
                text-3xl
                sm:text-4xl
                font-black
                text-slate-900
                tracking-tight
                leading-tight
              "
            >
              {contentData.title || title}
            </h1>

            {/* Subtitle */}

            <p
              className="
                text-xs
                sm:text-sm
                text-slate-400
                font-semibold
                mt-1
              "
            >
              {subtitle}
            </p>

            {/* Published Date */}

            {contentData.createdOn && (
              <div
                className="
                  flex
                  items-center
                  gap-1.5
                  text-[11px]
                  sm:text-xs
                  text-slate-400
                  font-mono
                  mt-2.5
                  bg-slate-100
                  w-fit
                  px-2
                  py-1
                  rounded-md
                "
              >
                <Calendar
                  size={12}
                  className="text-[#2F5E2E]"
                />

                <span>
                  Published On:{" "}
                  {contentData.createdOn.split(
                    "T"
                  )[0]}
                </span>
              </div>
            )}
          </div>

          {/* ======================================================
              CONTENT PANEL
          ====================================================== */}

          <div
            className="
              bg-white
              border
              border-slate-200
              rounded-xl
              shadow-sm
              p-4
              sm:p-5
              lg:p-6
            "
          >
            {/* ====================================================
                RICH TEXT CONTENT

                This renders the HTML generated by ReactQuill
                while keeping the frontend compact.
                ==================================================== */}

            <div
              className="
                text-[13px]
                sm:text-[14px]
                text-slate-600
                leading-[1.45]
                break-words
                whitespace-normal

                /* =================================================
                   PARAGRAPHS
                   ================================================= */

                [&_p]:m-0
                [&_p]:mb-3
                [&_p]:leading-[1.45]

                /* =================================================
                   H1
                   ================================================= */

                [&_h1]:text-[24px]
                [&_h1]:font-bold
                [&_h1]:leading-[1.2]
                [&_h1]:text-slate-900
                [&_h1]:mt-5
                [&_h1]:mb-3

                /* =================================================
                   H2
                   ================================================= */

                [&_h2]:text-[20px]
                [&_h2]:font-bold
                [&_h2]:leading-[1.25]
                [&_h2]:text-slate-900
                [&_h2]:mt-5
                [&_h2]:mb-2

                /* =================================================
                   H3
                   ================================================= */

                [&_h3]:text-[17px]
                [&_h3]:font-bold
                [&_h3]:leading-[1.3]
                [&_h3]:text-slate-900
                [&_h3]:mt-4
                [&_h3]:mb-2

                /* =================================================
                   BOLD
                   ================================================= */

                [&_strong]:font-bold
                [&_b]:font-bold
                [&_strong]:text-slate-900

                /* =================================================
                   ITALIC
                   ================================================= */

                [&_em]:italic
                [&_i]:italic

                /* =================================================
                   UNDERLINE
                   ================================================= */

                [&_u]:underline
                [&_u]:underline-offset-2

                /* =================================================
                   STRIKE
                   ================================================= */

                [&_s]:line-through
                [&_strike]:line-through

                /* =================================================
                   ORDERED LIST
                   ================================================= */

                [&_ol]:list-decimal
                [&_ol]:pl-5
                [&_ol]:mb-3
                [&_ol]:space-y-0.5

                /* =================================================
                   BULLET LIST
                   ================================================= */

                [&_ul]:list-disc
                [&_ul]:pl-5
                [&_ul]:mb-3
                [&_ul]:space-y-0.5

                /* =================================================
                   LIST ITEMS
                   ================================================= */

                [&_li]:leading-[1.45]
                [&_li]:pl-1
                [&_li]:mb-0.5

                /* =================================================
                   QUILL BULLET LIST
                   ================================================= */

                [&_li[data-list='bullet']]:list-disc

                /* =================================================
                   QUILL ORDERED LIST
                   ================================================= */

                [&_li[data-list='ordered']]:list-decimal

                /* =================================================
                   LINKS
                   ================================================= */

                [&_a]:text-[#2F5E2E]
                [&_a]:font-medium
                [&_a]:underline
                [&_a]:underline-offset-2
                hover:[&_a]:text-[#244A24]

                /* =================================================
                   BLOCKQUOTE
                   ================================================= */

                [&_blockquote]:border-l-2
                [&_blockquote]:border-slate-300
                [&_blockquote]:pl-3
                [&_blockquote]:my-3
                [&_blockquote]:italic
                [&_blockquote]:text-slate-500

                /* =================================================
                   IMAGES
                   ================================================= */

                [&_img]:max-w-full
                [&_img]:h-auto
                [&_img]:rounded-lg
                [&_img]:shadow-sm
                [&_img]:my-4

                /* =================================================
                   HORIZONTAL LINE
                   ================================================= */

                [&_hr]:border-0
                [&_hr]:border-t
                [&_hr]:border-slate-200
                [&_hr]:my-4

                /* =================================================
                   QUILL INDENT
                   ================================================= */

                [&_.ql-indent-1]:pl-8
                [&_.ql-indent-2]:pl-16
                [&_.ql-indent-3]:pl-24
                [&_.ql-indent-4]:pl-32

                /* =================================================
                   QUILL ALIGNMENT
                   ================================================= */

                [&_.ql-align-center]:text-center
                [&_.ql-align-right]:text-right
                [&_.ql-align-justify]:text-justify

                /* =================================================
                   TABLES IF CONTENT CONTAINS TABLES
                   ================================================= */

                [&_table]:w-full
                [&_table]:border-collapse
                [&_table]:my-4

                [&_th]:border
                [&_th]:border-slate-300
                [&_th]:px-3
                [&_th]:py-2
                [&_th]:font-bold
                [&_th]:text-left
                [&_th]:text-slate-900

                [&_td]:border
                [&_td]:border-slate-300
                [&_td]:px-3
                [&_td]:py-2

                /* =================================================
                   SMALL SCREEN
                   ================================================= */

                max-sm:[&_h1]:text-[21px]
                max-sm:[&_h2]:text-[18px]
                max-sm:[&_h3]:text-[16px]
              "
              dangerouslySetInnerHTML={{
                __html:
                  sanitizeAndCleanHtml(
                    contentData.content
                  ),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPageLayout;